import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { meetingAPI, recordingAPI } from '../services/api';
import PlayStorePrompt from '../components/PlayStorePrompt';
import { useWakeLock, useAudioKeepAlive } from '../hooks/useWakeLock';
import { MuteButton, AudioOutputToggle, VolumeControl } from '../components/AudioControls';
import { toast } from 'react-toastify';

import {
  LiveKitRoom,
  useParticipants,
  RoomAudioRenderer,
  useIsSpeaking,
  useTracks,
} from '@livekit/components-react';
import { Track, DisconnectReason } from 'livekit-client';
import '@livekit/components-styles';

import './MeetingRoom.css';

/* -------------------- Sound Alerts -------------------- */
// Plays a short synthesized chime + haptic. Distinct tone per event type.
//  - 'join'    : ascending two-tone (someone joined)
//  - 'leave'   : descending two-tone (someone left)
//  - 'waiting' : double mid-tone (guest awaiting approval)
function playChime(type = 'join') {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    // On mobile the context can start suspended — resume so scheduled tones actually play.
    if (ctx.state === 'suspended' && ctx.resume) ctx.resume();
    const now = ctx.currentTime;
    const tone = (freq, start, dur, vol = 0.4) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, now + start);
      gain.gain.setValueAtTime(vol, now + start);
      gain.gain.exponentialRampToValueAtTime(0.01, now + start + dur);
      osc.start(now + start);
      osc.stop(now + start + dur);
    };
    if (type === 'leave') {
      tone(880, 0, 0.22);
      tone(540, 0.16, 0.3);
    } else if (type === 'waiting') {
      tone(800, 0, 0.28);
      tone(1000, 0.2, 0.3);
    } else {
      // join (default) — friendly ascending
      tone(660, 0, 0.22);
      tone(990, 0.16, 0.32);
    }
    setTimeout(() => ctx.close().catch(() => {}), 1200);
  } catch {
    /* audio not available — ignore */
  }
  try {
    if ('vibrate' in navigator) {
      navigator.vibrate(type === 'leave' ? [90] : [120, 60, 120]);
    }
  } catch { /* ignore */ }
}

/* -------------------- Waiting Room Panel (Host Only) -------------------- */

function WaitingRoomPanel({ meetingId, isHost }) {
  const [waitingGuests, setWaitingGuests] = useState([]);
  const [loading, setLoading] = useState(false);
  const previousCountRef = useRef(0);

  const fetchWaitingGuests = useCallback(async () => {
    if (!isHost) return;

    try {
      const response = await meetingAPI.getWaitingRoom(meetingId);
      const newGuests = response.data.waiting_guests || [];

      // Check if there are new guests since last check
      if (newGuests.length > previousCountRef.current && previousCountRef.current >= 0) {
        const newCount = newGuests.length - previousCountRef.current;
        playChime('waiting');
        toast.info(`🔔 ${newCount} new guest${newCount > 1 ? 's' : ''} waiting for approval`, {
          autoClose: 5000,
        });
      }

      previousCountRef.current = newGuests.length;
      setWaitingGuests(newGuests);
    } catch (error) {
      console.error('Failed to fetch waiting room:', error);
    }
  }, [meetingId, isHost]);

  useEffect(() => {
    if (isHost) {
      fetchWaitingGuests();
      // Poll every 5 seconds
      const interval = setInterval(fetchWaitingGuests, 5000);
      return () => clearInterval(interval);
    }
  }, [isHost, fetchWaitingGuests]);

  // Reset count when component unmounts
  useEffect(() => {
    return () => {
      previousCountRef.current = 0;
    };
  }, [meetingId]);

  const handleApprove = async (waitingId, guestName) => {
    setLoading(true);
    try {
      await meetingAPI.approveGuest(meetingId, waitingId);
      toast.success(`${guestName} has been admitted`);
      fetchWaitingGuests();
    } catch (error) {
      toast.error('Failed to approve guest');
    }
    setLoading(false);
  };

  const handleReject = async (waitingId, guestName) => {
    setLoading(true);
    try {
      await meetingAPI.rejectGuest(meetingId, waitingId);
      toast.info(`${guestName} has been declined`);
      fetchWaitingGuests();
    } catch (error) {
      toast.error('Failed to reject guest');
    }
    setLoading(false);
  };

  if (!isHost || waitingGuests.length === 0) {
    return null;
  }

  return (
    <div className={`waiting-room-panel ${waitingGuests.length > 0 ? 'has-guests' : ''}`}>
      <h3>
        🚪 Waiting Room ({waitingGuests.length})
        {waitingGuests.length > 0 && <span className="pulse-dot"></span>}
      </h3>
      <div className="waiting-guests-list">
        {waitingGuests.map((guest) => (
          <div key={guest.id} className="waiting-guest-item">
            <span className="guest-name">{guest.guest_name}</span>
            <div className="guest-actions">
              <button
                onClick={() => handleApprove(guest.id, guest.guest_name)}
                disabled={loading}
                className="btn-approve"
              >
                ✓ Admit
              </button>
              <button
                onClick={() => handleReject(guest.id, guest.guest_name)}
                disabled={loading}
                className="btn-reject"
              >
                ✗ Decline
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* -------------------- Participant Visibility Controls (Host Only) -------------------- */

function VisibilityControls({ meetingId, dbParticipants, dbGuests, globalVisibility, setGlobalVisibility, onRefresh }) {

  const toggleGlobalVisibility = async () => {
    const newValue = !globalVisibility;
    // Confirm before exposing everyone — prevents accidental enable
    if (newValue && !window.confirm('Make ALL participants visible to everyone in this meeting?')) {
      return;
    }
    try {
      await meetingAPI.toggleGlobalVisibility(meetingId, newValue);
      setGlobalVisibility(newValue);
      toast.success(`Participant visibility ${newValue ? 'enabled' : 'disabled'} for all`);
      onRefresh();
    } catch (error) {
      toast.error('Failed to update visibility');
    }
  };

  const toggleParticipantVisibility = async (participantId, currentValue) => {
    try {
      await meetingAPI.toggleParticipantVisibility(meetingId, participantId, !currentValue);
      toast.success('Participant visibility updated');
      onRefresh();
    } catch (error) {
      toast.error('Failed to update visibility');
    }
  };

  const toggleGuestVisibility = async (guestId, currentValue) => {
    try {
      await meetingAPI.toggleGuestVisibility(meetingId, guestId, !currentValue);
      toast.success('Guest visibility updated');
      onRefresh();
    } catch (error) {
      toast.error('Failed to update visibility');
    }
  };

  return (
    <div className="visibility-controls">
      <h4>👁️ Visibility Controls</h4>

      <div className="global-toggle">
        <span className="global-toggle__label">Show participants to everyone</span>
        <button
          type="button"
          className={`btn-global-toggle ${globalVisibility ? 'on' : 'off'}`}
          onClick={(e) => { e.stopPropagation(); toggleGlobalVisibility(); }}
        >
          {globalVisibility ? '✓ ON' : 'OFF'}
        </button>
      </div>

      {!globalVisibility && (
        <div className="individual-permissions" style={{ marginTop: 10 }}>
          <h5>Individual Permissions:</h5>

          {dbParticipants.filter(p => !p.is_host).length === 0 && dbGuests.length === 0 && (
            <p className="no-participants-msg">No other participants yet</p>
          )}

          {dbParticipants.filter(p => !p.is_host).map(p => (
            <div key={p.id} className="visibility-item">
              <span>{p.name || p.email}</span>
              <button
                type="button"
                className={`btn-visibility ${p.can_see_participants ? 'enabled' : 'disabled'}`}
                onClick={(e) => { e.stopPropagation(); toggleParticipantVisibility(p.id, p.can_see_participants); }}
              >
                {p.can_see_participants ? '👁️ Can See' : '🚫 Hidden'}
              </button>
            </div>
          ))}

          {dbGuests.map(g => (
            <div key={g.id} className="visibility-item">
              <span>{g.guest_name} (Guest)</span>
              <button
                type="button"
                className={`btn-visibility ${g.can_see_participants ? 'enabled' : 'disabled'}`}
                onClick={(e) => { e.stopPropagation(); toggleGuestVisibility(g.id, g.can_see_participants); }}
              >
                {g.can_see_participants ? '👁️ Can See' : '🚫 Hidden'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* -------------------- Single Participant Item -------------------- */

function ParticipantItem({ participant }) {
  const isSpeaking = useIsSpeaking(participant);
  
  // Get display name - prefer name over identity
  const displayName = participant.name || participant.identity || 'Unknown';
  const isLocal = participant.isLocal;

  return (
    <div className="participant-item">
      <span className="participant-name">
        {displayName}
        {isLocal && ' (You)'}
      </span>
      <span className={`participant-status ${isSpeaking ? 'speaking' : ''}`}>
        {isSpeaking ? (
          <>
            <span className="speaking-bars"><span></span><span></span><span></span></span>
            Speaking
          </>
        ) : 'Muted'}
      </span>
    </div>
  );
}

/* -------------------- Participants List -------------------- */

function ParticipantList({ isHost, canSeeParticipants, meetingId }) {
  const participants = useParticipants();
  const prevParticipantsRef = useRef([]);
  const [dbParticipants, setDbParticipants] = useState([]);
  const [dbGuests, setDbGuests] = useState([]);
  const [globalVisibility, setGlobalVisibility] = useState(false);
  const [kickingId, setKickingId] = useState(null);

  const fetchParticipants = useCallback(async () => {
    try {
      const response = await meetingAPI.getParticipants(meetingId);
      if (response.data.success) {
        setDbParticipants(response.data.participants || []);
        setDbGuests(response.data.guests || []);
      }
    } catch (error) {
      console.error('Failed to fetch participants:', error);
    }
  }, [meetingId]);

  const fetchMeetingSettings = useCallback(async () => {
    try {
      const response = await meetingAPI.get(meetingId);
      if (response.data.success) {
        setGlobalVisibility(response.data.meeting.show_participants_to_all || false);
      }
    } catch (error) {
      console.error('Failed to fetch meeting settings:', error);
    }
  }, [meetingId]);

  // Detect participants joining / leaving and alert the host (sound + toast).
  // hasInitializedRef prevents alerting for participants already present on first render.
  const hasInitializedRef = useRef(false);
  useEffect(() => {
    if (!isHost) return;
    const prev = prevParticipantsRef.current;
    const prevIds = new Set(prev.map((p) => p.identity));
    const curIds = new Set(participants.map((p) => p.identity));

    if (hasInitializedRef.current) {
      // Joins: present now, absent before
      participants.forEach((cur) => {
        if (cur.isLocal) return;
        if (!prevIds.has(cur.identity)) {
          playChime('join');
          toast.success(`🔔 ${cur.name || cur.identity} joined the meeting`, { autoClose: 5000 });
        }
      });
      // Leaves: present before, absent now
      prev.forEach((p) => {
        if (p.isLocal) return;
        if (!curIds.has(p.identity)) {
          playChime('leave');
          toast.info(`👋 ${p.name || p.identity} left the meeting`, { autoClose: 5000 });
        }
      });
    } else {
      hasInitializedRef.current = true;
    }

    prevParticipantsRef.current = participants;
  }, [participants, isHost]);

  const handleKickParticipant = async (participantDbId, name) => {
    if (!window.confirm(`Remove ${name} from the meeting?`)) return;
    setKickingId(participantDbId);
    try {
      await meetingAPI.kickParticipant(meetingId, participantDbId);
      toast.success(`${name} has been removed`);
      fetchParticipants();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to remove participant');
    } finally {
      setKickingId(null);
    }
  };

  const handleKickGuest = async (guestId, name) => {
    if (!window.confirm(`Remove ${name} from the meeting?`)) return;
    setKickingId(`guest_${guestId}`);
    try {
      await meetingAPI.kickGuest(meetingId, guestId);
      toast.success(`${name} has been removed`);
      fetchParticipants();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to remove guest');
    } finally {
      setKickingId(null);
    }
  };

  useEffect(() => {
    if (isHost) {
      fetchParticipants();
      fetchMeetingSettings();
      const interval = setInterval(fetchParticipants, 5000);
      return () => clearInterval(interval);
    }
  }, [isHost, meetingId, fetchParticipants, fetchMeetingSettings]);

  // Host always sees everything
  if (isHost) {
    return (
      <div className="participants-list">
        <h3>👥 Participants ({participants.length})</h3>

        {participants.length === 0 ? (
          <p className="no-participants">Waiting for participants...</p>
        ) : (
          participants.map((participant) => (
            <ParticipantItem key={participant.sid} participant={participant} />
          ))
        )}

        {/* Host controls: kick + visibility */}
        {(dbParticipants.filter(p => !p.is_host).length > 0 || dbGuests.length > 0) && (
          <div className="host-participant-controls">
            <h4 style={{ margin: '10px 0 6px', fontSize: '0.82rem', color: '#94a3b8' }}>Manage Participants</h4>
            {dbParticipants.filter(p => !p.is_host).map(p => (
              <div key={p.id} className="participant-manage-row">
                <span className="participant-manage-name">{p.name || p.email}</span>
                <button
                  type="button"
                  className="btn-kick"
                  disabled={kickingId === p.id}
                  onClick={() => handleKickParticipant(p.id, p.name || p.email)}
                >
                  {kickingId === p.id ? '…' : '⛔ Remove'}
                </button>
              </div>
            ))}
            {dbGuests.map(g => (
              <div key={g.id} className="participant-manage-row">
                <span className="participant-manage-name">{g.guest_name} (Guest)</span>
                <button
                  type="button"
                  className="btn-kick"
                  disabled={kickingId === `guest_${g.id}`}
                  onClick={() => handleKickGuest(g.id, g.guest_name)}
                >
                  {kickingId === `guest_${g.id}` ? '…' : '⛔ Remove'}
                </button>
              </div>
            ))}
          </div>
        )}

        <VisibilityControls
          meetingId={meetingId}
          dbParticipants={dbParticipants}
          dbGuests={dbGuests}
          globalVisibility={globalVisibility}
          setGlobalVisibility={setGlobalVisibility}
          onRefresh={fetchParticipants}
        />
      </div>
    );
  }

  // Non-host: check if they can see participants
  if (!canSeeParticipants) {
    return (
      <div className="participants-list">
        <h3>👥 Participants</h3>
        <p className="visibility-notice">
          🔒 Participant list is hidden by the host
        </p>
        <p className="participant-count">
          {participants.length} participant(s) in meeting
        </p>
      </div>
    );
  }

  return (
    <div className="participants-list">
      <h3>Participants ({participants.length})</h3>
      {participants.map((participant) => (
        <ParticipantItem key={participant.sid} participant={participant} />
      ))}
    </div>
  );
}

/* -------------------- Room Content -------------------- */

function RoomContent({ meetingId, isHost, canSeeParticipants }) {
  const remoteTracks = useTracks([Track.Source.Microphone], { onlySubscribed: true });

  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [uploading, setUploading] = useState(false);

  const timerRef = useRef(null);
  const finalDurationRef = useRef(0);
  const localMicStreamRef = useRef(null);
  const audioContextRef = useRef(null);     // Web Audio mixing context
  const destinationRef = useRef(null);      // MediaStreamAudioDestinationNode (the mix output)
  const mixedRecorderRef = useRef(null);    // single MediaRecorder on the mixed stream
  const mixedChunksRef = useRef([]);        // chunks from the mixed recorder
  const connectedSourcesRef = useRef(new Set()); // identities already wired into the mix
  const isRecordingRef = useRef(false);     // mirrors isRecording for unmount cleanup

  // Wake Lock and Audio Keep Alive for background audio
  const { requestWakeLock, releaseWakeLock } = useWakeLock();
  const { startKeepAlive, stopKeepAlive } = useAudioKeepAlive();

  // Activate wake lock and audio keep-alive when component mounts
  useEffect(() => {
    requestWakeLock();
    startKeepAlive();
    
    return () => {
      releaseWakeLock();
      stopKeepAlive();
    };
  }, [requestWakeLock, releaseWakeLock, startKeepAlive, stopKeepAlive]);

  // Keep ref in sync for unmount cleanup
  useEffect(() => { isRecordingRef.current = isRecording; }, [isRecording]);

  // When a new remote participant joins during an active recording, wire them into the mix
  useEffect(() => {
    if (!isRecording || !audioContextRef.current || !destinationRef.current) return;
    remoteTracks.forEach(({ publication, participant }) => {
      if (!publication || !participant || participant.isLocal) return;
      const track = publication.track;
      if (!track?.mediaStreamTrack) return;
      const identity = String(participant.identity);
      if (connectedSourcesRef.current.has(identity)) return;
      connectedSourcesRef.current.add(identity);
      try {
        const remoteStream = new MediaStream([track.mediaStreamTrack]);
        const source = audioContextRef.current.createMediaStreamSource(remoteStream);
        source.connect(destinationRef.current);
      } catch { /* track may have ended */ }
    });
  }, [remoteTracks, isRecording]);

  const startRecording = async () => {
    try {
      // Web Audio API: mix ALL participants into one stream, record once
      const audioContext = new AudioContext();
      const destination = audioContext.createMediaStreamDestination();

      // Local mic
      const localStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      localMicStreamRef.current = localStream;
      const localSource = audioContext.createMediaStreamSource(localStream);
      localSource.connect(destination);

      // All remote participants currently in the room
      connectedSourcesRef.current = new Set(['local']);
      remoteTracks.forEach(({ publication, participant }) => {
        if (!publication || !participant || participant.isLocal) return;
        const track = publication.track;
        if (!track?.mediaStreamTrack) return;
        const identity = String(participant.identity);
        if (connectedSourcesRef.current.has(identity)) return;
        connectedSourcesRef.current.add(identity);
        try {
          const remoteStream = new MediaStream([track.mediaStreamTrack]);
          const source = audioContext.createMediaStreamSource(remoteStream);
          source.connect(destination);
        } catch { /* ignore */ }
      });

      audioContextRef.current = audioContext;
      destinationRef.current = destination;

      // Single recorder on the mixed output
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';
      mixedChunksRef.current = [];
      const recorder = new MediaRecorder(destination.stream, { mimeType });
      recorder.ondataavailable = (e) => { if (e.data.size > 0) mixedChunksRef.current.push(e.data); };
      mixedRecorderRef.current = recorder;
      recorder.start(1000);

      setIsRecording(true);
      setRecordingTime(0);
      finalDurationRef.current = 0;
      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => {
          const next = prev + 1;
          finalDurationRef.current = next;
          return next;
        });
      }, 1000);

      toast.success('Recording started — capturing all participants!');
    } catch (error) {
      console.error('Failed to start recording:', error);
      toast.error('Failed to access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (!isRecording) return;

    setIsRecording(false);
    clearInterval(timerRef.current);
    timerRef.current = null;

    if (localMicStreamRef.current) {
      localMicStreamRef.current.getTracks().forEach((t) => t.stop());
      localMicStreamRef.current = null;
    }

    const recorder = mixedRecorderRef.current;
    if (!recorder || recorder.state === 'inactive') {
      toast.error('Recording is empty. Please try again.');
      audioContextRef.current?.close();
      audioContextRef.current = null;
      destinationRef.current = null;
      return;
    }

    recorder.onstop = () => {
      const blob = new Blob(mixedChunksRef.current, { type: 'audio/webm' });
      audioContextRef.current?.close();
      audioContextRef.current = null;
      destinationRef.current = null;
      if (blob.size === 0) {
        toast.error('Recording is empty. Please try again.');
        return;
      }
      uploadMixedRecording(blob);
    };
    try { recorder.stop(); } catch { /* already stopping */ }
  };

  const uploadMixedRecording = async (blob) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('audio', blob, `recording_${Date.now()}.webm`);
      formData.append('meetingId', meetingId);
      formData.append('duration', finalDurationRef.current);
      await recordingAPI.upload(formData);
      toast.success(`✅ Recording saved! Duration: ${formatTime(finalDurationRef.current)}`);
      setRecordingTime(0);
      finalDurationRef.current = 0;
    } catch (error) {
      console.error('Failed to upload recording:', error);
      toast.error('Failed to save recording. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Cleanup on unmount — stop mic and AudioContext
  useEffect(() => {
    return () => {
      clearInterval(timerRef.current);
      if (localMicStreamRef.current) {
        localMicStreamRef.current.getTracks().forEach((t) => t.stop());
      }
      // If recording was active when unmounted, stop the recorder and upload
      if (isRecordingRef.current && mixedRecorderRef.current && mixedRecorderRef.current.state !== 'inactive') {
        const recorder = mixedRecorderRef.current;
        const chunks = mixedChunksRef.current;
        const duration = finalDurationRef.current;
        const mid = meetingId;
        recorder.onstop = () => {
          const blob = new Blob(chunks, { type: 'audio/webm' });
          audioContextRef.current?.close();
          if (blob.size === 0) return;
          const formData = new FormData();
          formData.append('audio', blob, `recording_${Date.now()}.webm`);
          formData.append('meetingId', mid);
          formData.append('duration', duration);
          const token = localStorage.getItem('token');
          const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000';
          fetch(`${apiUrl}/api/recordings/upload`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            body: formData,
          }).catch(console.error);
        };
        try { recorder.stop(); } catch { /* already stopping */ }
      } else {
        audioContextRef.current?.close();
      }
    };
  }, [meetingId]);

  return (
    <div className="room-container">

      <div className="audio-info">
        <h2>🎙️ Audio Meeting</h2>
        <p className="status-connected">Connected & Live</p>
        {isHost && <span className="host-badge">👑 Host</span>}
        <p className="info-text">
          Speak into your microphone to participate in the meeting
        </p>
      </div>

      {/* Audio Controls Bar */}
    <div className="audio-controls-bar">
      <MuteButton />
      <AudioOutputToggle />
      <VolumeControl />
    </div>

      {isHost && (
        <div className="recording-controls">
          {uploading ? (
            <div className="uploading-indicator">
              <div className="spinner-small"></div>
              <span>⏳ Uploading recording...</span>
            </div>
          ) : isRecording ? (
            <div className="recording-active">
              <div className="recording-indicator">
                <span className="recording-dot"></span>
                <span className="recording-time">
                  REC {formatTime(recordingTime)}
                </span>
              </div>
              <button onClick={stopRecording} className="btn-stop-recording">
                ⏹ Stop & Save
              </button>
            </div>
          ) : (
            <button onClick={startRecording} className="btn-start-recording">
              ⏺ Start Recording
            </button>
          )}
        </div>
      )}

      {/* Waiting Room Panel - Only visible to host */}
      {isHost && <WaitingRoomPanel meetingId={meetingId} isHost={isHost} />}

      <ParticipantList 
        isHost={isHost} 
        canSeeParticipants={canSeeParticipants} 
        meetingId={meetingId}
      />
      <RoomAudioRenderer />
    </div>
  );
}

/* -------------------- Main Page -------------------- */

function MeetingRoom() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [meeting, setMeeting] = useState(null);
  const [token, setToken] = useState('');
  const [livekitUrl, setLivekitUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isHost, setIsHost] = useState(false);
  const [canSeeParticipants, setCanSeeParticipants] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  // True when this (non-host) user is waiting in the host's waiting room for approval.
  const [waitingApproval, setWaitingApproval] = useState(false);
  // When true, LiveKitRoom disconnects immediately — stops this user's audio
  // BEFORE the feedback popup is shown (otherwise they keep streaming).
  const [disconnected, setDisconnected] = useState(false);
  const postFeedbackDest = useRef('/dashboard');
  // Tracks intentional leave/end so onDisconnected doesn't double-navigate
  const intentionalLeaveRef = useRef(false);

  const joinMeeting = useCallback(async () => {
    try {
      const response = await meetingAPI.join(id);

      // Non-host awaiting host approval in the waiting room
      if (response.data.status === 'waiting') {
        setWaitingApproval(true);
        setLoading(false);
        return;
      }

      // Check if meeting has ended
      if (response.data.meeting.status === 'ended') {
        setError('This meeting has ended and cannot be joined.');
        setLoading(false);
        return;
      }

      // Approved (or host) — enter the room
      setWaitingApproval(false);
      setMeeting(response.data.meeting);
      setToken(response.data.token);
      setLivekitUrl(response.data.livekit_url);
      setIsHost(response.data.is_host);
      setCanSeeParticipants(response.data.can_see_participants);
      setLoading(false);
      toast.success('Joined meeting successfully!');
    } catch (err) {
      const errMsg = err.response?.data?.error || '';
      if (errMsg === 'Your request to join was declined by the host') {
        setWaitingApproval(false);
        setError('Your request to join was declined by the host.');
      } else if (errMsg === 'Meeting has ended') {
        setError('This meeting has ended and cannot be joined.');
      } else if (errMsg === 'Meeting window has not opened yet') {
        setError('This meeting window has not opened yet. Please wait until the scheduled start time.');
      } else if (errMsg === 'Meeting window has expired') {
        setError('The meeting window has expired. Please contact the host.');
      } else if (err.response?.data?.code === 'TRIAL_EXPIRED') {
        navigate('/pricing?reason=trial_expired');
        return;
      } else {
        setError('Failed to join meeting. Please check your connection.');
      }
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    joinMeeting();
  }, [joinMeeting]);

  // While waiting for host approval, re-attempt the join every 4s.
  // Once the host approves, the join returns a token and we enter the room.
  useEffect(() => {
    if (!waitingApproval) return;
    const interval = setInterval(() => { joinMeeting(); }, 4000);
    return () => clearInterval(interval);
  }, [waitingApproval, joinMeeting]);

  const handleEndMeeting = async () => {
    if (window.confirm('Are you sure you want to end this meeting for everyone?')) {
      try {
        intentionalLeaveRef.current = true;
        setDisconnected(true); // stop my audio immediately
        await meetingAPI.end(id);
        toast.success('Meeting ended');
        postFeedbackDest.current = '/dashboard';
        setShowFeedback(true);
      } catch {
        intentionalLeaveRef.current = false;
        setDisconnected(false);
        toast.error('Failed to end meeting');
      }
    }
  };

  const handleLeave = async () => {
    intentionalLeaveRef.current = true;
    setDisconnected(true); // stop my audio immediately, before popup
    try {
      await meetingAPI.leave(id);
    } catch (error) {
      console.error('Failed to leave meeting:', error);
    }
    postFeedbackDest.current = '/dashboard';
    setShowFeedback(true);
  };

  // Poll meeting status every 10s for participants (non-host):
  // detects visibility changes and meeting-ended state without requiring a page refresh.
  useEffect(() => {
    if (isHost || !meeting) return;
    const poll = async () => {
      try {
        const { data } = await meetingAPI.getStatus(id);
        setCanSeeParticipants(data.can_see_participants);
        if (data.status === 'ended' && !intentionalLeaveRef.current) {
          intentionalLeaveRef.current = true;
          navigate('/meeting-ended', {
            state: { meetingTitle: meeting?.title, reason: 'ended' },
          });
        }
      } catch {
        // ignore transient poll errors
      }
    };
    const interval = setInterval(poll, 10000);
    return () => clearInterval(interval);
  }, [id, isHost, meeting, navigate]);

  if (loading) {
    return (
      <motion.div
        className="meeting-room loading-screen"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
      >
        <div className="spinner"></div>
        <h2>Joining meeting...</h2>
      </motion.div>
    );
  }

  // Waiting for the host to approve this user from the waiting room
  if (waitingApproval) {
    return (
      <motion.div
        className="meeting-room loading-screen"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
      >
        <div className="spinner"></div>
        <h2>Waiting for host approval…</h2>
        <p style={{ color: 'rgba(255,255,255,0.6)', marginTop: 8, textAlign: 'center', maxWidth: 360 }}>
          The host has been notified of your request to join. You'll enter automatically once approved.
        </p>
        <button
          onClick={() => navigate('/dashboard')}
          className="btn-primary"
          style={{ marginTop: 20 }}
        >
          Cancel
        </button>
      </motion.div>
    );
  }

  if (error || !token) {
    return (
      <motion.div
        className="meeting-room error-screen"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        <h2>{error || 'Authentication Error'}</h2>
        <p>You cannot join this meeting.</p>
        <button onClick={() => navigate('/dashboard')} className="btn-primary">
          Back to Dashboard
        </button>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="meeting-room"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.15 } }}
      transition={{ duration: 0.3 }}
    >
      <header className="meeting-header">
        <div className="meeting-info">
          <h1>🎤 {meeting.title}</h1>
          <p className="room-id">Room ID: {meeting.room_id}</p>
        </div>

        <div className="meeting-controls header-actions">
          <button onClick={handleLeave} className="btn-leave">
            ← Leave Meeting
          </button>
          {isHost && (
            <button onClick={handleEndMeeting} className="btn-end">
              ⏹ End Meeting
            </button>
          )}
        </div>
      </header>

      <LiveKitRoom
        token={token}
        serverUrl={livekitUrl}
        connect={!disconnected}
        audio={!disconnected}
        video={false}
        onDisconnected={(reason) => {
          if (intentionalLeaveRef.current) return;
          if (reason === DisconnectReason.ROOM_DELETED) {
            intentionalLeaveRef.current = true;
            navigate('/meeting-ended', {
              state: { meetingTitle: meeting?.title, reason: 'ended' },
            });
          } else if (reason === DisconnectReason.PARTICIPANT_REMOVED) {
            intentionalLeaveRef.current = true;
            navigate('/meeting-ended', {
              state: { meetingTitle: meeting?.title, reason: 'removed' },
            });
          }
        }}
      >
        <RoomContent
          meetingId={id}
          isHost={isHost}
          canSeeParticipants={canSeeParticipants}
        />
      </LiveKitRoom>

      <PlayStorePrompt
        isOpen={showFeedback}
        onDone={() => { setShowFeedback(false); navigate(postFeedbackDest.current); }}
      />
    </motion.div>
  );
}

export default MeetingRoom;